/**
 * 権限チェックユーティリティ
 *
 * ユーザーの権限判定を行うヘルパー関数を提供
 */
import {
  ROLE_IDS,
  SITE_RESTRICTED_ROLES,
  VIEW_ONLY_ROLES,
  ADMIN_ROLES,
  type RoleId,
} from '@/domain/constants/roles';

interface UserWithRole {
  roleId: number;
  siteId?: { toPrimitive: () => number } | number | null;
}

/**
 * サイト制限が適用されるロールか判定
 */
export function isSiteRestricted(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return SITE_RESTRICTED_ROLES.includes(user.roleId as RoleId);
}

/**
 * 閲覧専用ロールか判定
 */
export function isViewOnly(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return VIEW_ONLY_ROLES.includes(user.roleId as RoleId);
}

/**
 * 管理者ロールか判定
 */
export function isAdmin(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return ADMIN_ROLES.includes(user.roleId as RoleId);
}

/**
 * 開発者ロール (DEV_USER) か判定
 *
 * v1.5: 発注一覧のアーカイブ(キャンセル済発注)閲覧は DEV_USER のみ。
 */
export function isDev(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return user.roleId === ROLE_IDS.DEV_USER;
}

/**
 * 在庫編集権限があるか判定
 * SITE_VIEWERは編集不可
 */
export function canEditInventory(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return !VIEW_ONLY_ROLES.includes(user.roleId as RoleId);
}

/**
 * 設定画面アクセス権限があるか判定
 */
export function canAccessSettings(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return user.roleId === ROLE_IDS.DEV_USER || user.roleId === ROLE_IDS.MCM_USER;
}

/**
 * パネル編集権限があるか判定
 */
export function canEditPanel(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return user.roleId === ROLE_IDS.DEV_USER || user.roleId === ROLE_IDS.MCM_USER;
}

/**
 * 在庫閾値設定権限があるか判定
 * DEV_USER、MCM_USER、SITE_USERが許可
 * SITE_VIEWERは不可
 */
export function canEditInventoryThreshold(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return (
    user.roleId === ROLE_IDS.DEV_USER ||
    user.roleId === ROLE_IDS.MCM_USER ||
    user.roleId === ROLE_IDS.SITE_USER
  );
}

/**
 * 現場ユーザー（SITE_USER）か判定
 * SITE_VIEWERは含まない
 */
export function isSiteUser(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return user.roleId === ROLE_IDS.SITE_USER;
}

/**
 * 現場閲覧者（SITE_VIEWER）か判定
 */
export function isSiteViewer(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return user.roleId === ROLE_IDS.SITE_VIEWER;
}

/**
 * 盤データCSVエクスポート権限があるか判定
 * SITE_VIEWERはエクスポート不可
 */
export function canExportPanelData(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return !VIEW_ONLY_ROLES.includes(user.roleId as RoleId);
}

// ========== 発注ワークフロー権限ヘルパー ==========
//
// 設計書 07-permissions.md §3 / 04-api-design.md §4.1 に整合。
// 4 編集系権限および管理リンク表示権限は全て [DEV_USER, MCM_USER, SITE_USER](SITE_VIEWER 除外)。
// SITE_VIEWER は閲覧専用ロールのため、編集系操作の動線(リンク・メニュー)は表示しない方針
// (直接 URL を入力した場合の閲覧自体はサーバー側 VIEW_INVENTORY 権限で許可される)。
// サイト境界(SITE_USER は自サイトのみ)はサーバー側 2 段階ガードで担保するため、
// クライアント側ヘルパーはロール権限のみを判定し、サイト判定は行わない。

/**
 * 発注登録(発注書作成)権限があるか判定
 *
 * SITE_VIEWER は不可、その他は可能(サイト境界はサーバーで判定)。
 * 設計書 07-permissions.md §6.1 推奨により `canEditInventory` を流用する。
 */
export function canCreatePurchaseOrder(user: UserWithRole | null): boolean {
  return canEditInventory(user);
}

/**
 * 着荷確認権限があるか判定
 *
 * SITE_VIEWER は不可、その他は可能。
 */
export function canConfirmArrival(user: UserWithRole | null): boolean {
  return canEditInventory(user);
}

/**
 * 着荷取消(巻き戻し)権限があるか判定
 *
 * SITE_VIEWER は不可、その他は可能。
 */
export function canReverseArrival(user: UserWithRole | null): boolean {
  return canEditInventory(user);
}

/**
 * 発注キャンセル権限があるか判定
 *
 * SITE_VIEWER は不可、その他は可能。
 */
export function canCancelPurchaseOrder(user: UserWithRole | null): boolean {
  return canEditInventory(user);
}

/**
 * 発注管理画面リンク表示権限があるか判定
 *
 * 設計書 07-permissions.md §6.1 の定義どおり SITE_VIEWER 以外で true。
 * 「在庫管理画面の『発注管理へ』リンク」「サイドメニュー『発注管理』項目」の双方で
 * 表示制御に使用する。SITE_VIEWER は閲覧専用ロールであり、編集系操作の動線(リンク・メニュー)を
 * 露出させない方針。直接 URL を入力した場合の閲覧自体はサーバー側 VIEW_INVENTORY 権限で許可される。
 */
export function canViewPurchaseOrderManagementLink(user: UserWithRole | null): boolean {
  if (user === null) {
    return false;
  }
  return !VIEW_ONLY_ROLES.includes(user.roleId as RoleId);
}
